/**
 * LiveMicDialog — la boîte du micro, en Dialogue : celle qui le demande, puis celle qui le dit refusé
 * (`dialog`, décidé par `liveMicSurface`).
 *
 *  - **« Active ton micro »** (`micGate` du moteur) : le micro attend le geste de l'élève — le live
 *    rouvert par un rechargement, sans que la page l'ait obtenu. « Activer mon micro » le demande dans
 *    le toucher ; « Plus tard » la ferme jusqu'au prochain Dialogue (le micro de la barre le demande
 *    aussi).
 *  - **« Ton micro est bloqué »** (`micRefusal`) : au web, le guide du déblocage propre au navigateur,
 *    un geste par ligne, puis « Recharger la page » et « Plus tard ». Au téléphone, « Réessayer » dans
 *    le geste ; refusé encore, « Ouvrir les réglages » et « Réessayer » (rien ne s'y recharge). « Plus
 *    tard », ou le fond touché, la ferme jusqu'au prochain refus que l'élève provoque, ou au prochain
 *    Dialogue.
 *
 * Un live rouvert par un rechargement l'a été sans geste : le navigateur y a souvent aussi mis le son
 * en pause. Le toucher qui demande le micro le relance dans le même geste, et la boîte du son
 * (`LiveAudio`) s'efface derrière celle-ci : un toucher règle les deux.
 */
import { useRoomContext } from "@livekit/components-react"
import React from "react"
import { Linking } from "react-native"

import { ConfirmDialog } from "../../../../../../design-system"
import { DeviceCheckGuidance } from "../../../../../../design-system/student"
import { type MicRefusalView } from "../../../../../../engine/live/student/micRefusal"
import { pagePermissionBrowser } from "../../../../../../engine/precall/browserPermissionPaths"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { type EngineTelemetry } from "../../../../../../engine/shared/telemetry"
import { liveMicAskCopy, liveMicRefusedCopy } from "../../../../view-models/student/Live/studentLiveViewModel"

/** Le navigateur, lu une fois : il ne change pas en route. */
const BROWSER = pagePermissionBrowser()

export type LiveMicDialogProps = {
    /** La boîte à l'écran, ou aucune (`liveMicSurface`). */
    dialog: "refused" | "ask" | null
    refusal: MicRefusalView
    /** Le prénom du masteur, que la demande nomme. */
    tutorFirstname: string | null | undefined
    /** « Activer mon micro » : appelé dans le geste. */
    onActivate: () => void
    onDismissAsk: () => void
    /** Redemande le micro : appelé dans le geste. */
    onRetry: () => void
    onDismiss: () => void
    telemetry: EngineTelemetry
}

export function LiveMicDialog(props: LiveMicDialogProps) {
    const { dialog, refusal, tutorFirstname, onActivate, onDismissAsk, onRetry, onDismiss, telemetry } = props
    const room = useRoomContext()
    // Le son du live mis en pause par le navigateur repart dans le même geste.
    const inGesture = (request: () => void) => () => {
        request()
        if (!room.canPlaybackAudio) {
            room.startAudio().catch((cause: unknown) => telemetry.error("start_audio", cause))
        }
    }
    const openSettings = () => {
        telemetry.transition("mic_refused", "mic_refused", IS_WEB ? "reload" : "open_settings")
        if (IS_WEB) window.location.reload()
        else Linking.openSettings().catch((cause: unknown) => telemetry.error("open_settings", cause))
    }
    const retry = inGesture(onRetry)

    const refusedCopy =
        dialog === "refused"
            ? liveMicRefusedCopy(refusal.step, { web: IS_WEB, browser: BROWSER, blocker: refusal.blocker })
            : null
    const copy = refusedCopy ?? liveMicAskCopy(tutorFirstname)
    const onConfirm = refusedCopy == null ? inGesture(onActivate) : refusal.step === "settings" ? openSettings : retry
    const onCancel = refusedCopy == null ? onDismissAsk : refusedCopy.cancel === "retry" ? retry : onDismiss

    return (
        <ConfirmDialog
            visible={dialog != null}
            title={copy.title}
            message={copy.message}
            confirmLabel={copy.confirmLabel}
            cancelLabel={copy.cancelLabel}
            danger={false}
            onConfirm={onConfirm}
            onCancel={onCancel}
            onDismiss={refusedCopy != null ? onDismiss : onDismissAsk}
            testID={refusedCopy != null ? "live-mic-refused" : "live-mic-ask"}
        >
            {refusedCopy?.steps != null && <DeviceCheckGuidance lines={refusedCopy.steps} />}
        </ConfirmDialog>
    )
}
